/**
 * SharedNoteService — manages the user's "Shared with me" inbox on web.
 *
 * Mirrors the Swift implementation in `apps/swift/Sources/Caliu/Services/
 * SharedNoteService.swift`. Resolves share deep links, fetches the public
 * payload, and persists the pointer + content cache locally. Sync v2 carries
 * the pointer rows (`shared_note_ref` entity); content is fetched on demand
 * from `GET /public/share/:id`.
 */

import { generateId } from "@caliu/utils";
import * as Sentry from "@sentry/react";
import { ApiError, apiFetchOrThrow } from "../../api/client";
import { enqueueSyncOp, getNote } from "../db";
import { execute, notifyChanges, queryOne, transaction } from "../sqlite";

// ── Types ────────────────────────────────────────────────────────────────

export interface SharedNotePayload {
  id: string;
  title: string;
  content: string;
  authorName: string;
  createdAt: string;
  updatedAt: string;
  mentions?: Record<string, { title: string }>;
}

export interface SharedNoteRow {
  id: string;
  title: string;
  content: string;
  authorName: string;
  createdAt: string;
  updatedAt: string;
  fetchedAt: string;
  mentionsJson: string | null;
}

export interface SharedNoteRefRow {
  id: string;
  userId: string;
  noteId: string;
  addedAt: string;
}

export type ResolveResult =
  | { kind: "openOwnedNote"; id: string }
  | { kind: "openSharedNote"; id: string };

export class SharedNoteError extends Error {
  constructor(
    public readonly code: "notFound" | "fetchFailed" | "storageFailed",
    message: string,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = "SharedNoteError";
  }
}

// ── Service ──────────────────────────────────────────────────────────────

/**
 * Resolve a share deep-link `id` for the currently authenticated user.
 *
 * If the user owns the note locally, route to the regular editor (the share
 * URL is just an alternate entry point for the owner). Otherwise fetch the
 * public payload, persist into shared_notes (content cache) + shared_note_refs
 * (synced pointer), and enqueue a sync push.
 */
export async function resolveShared(
  id: string,
  userId: string,
): Promise<ResolveResult> {
  const local = getNote(id);
  if (local && local.userId === userId && !local.deletedAt) {
    return { kind: "openOwnedNote", id };
  }

  let payload: SharedNotePayload;
  try {
    payload = await apiFetchOrThrow<SharedNotePayload>(`/public/share/${id}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      throw new SharedNoteError("notFound", "Shared note not found", err);
    }
    Sentry.captureException(err);
    throw new SharedNoteError("fetchFailed", "Failed to fetch shared note", err);
  }

  try {
    persistSharedNote(payload, userId);
  } catch (err) {
    Sentry.captureException(err);
    throw new SharedNoteError(
      "storageFailed",
      "Failed to cache shared note",
      err,
    );
  }

  return { kind: "openSharedNote", id: payload.id };
}

/**
 * Refresh a cached shared note's content from the worker. Used when the user
 * re-opens a stale entry. Throws `notFound` if the note is no longer shared.
 */
export async function refreshShared(id: string, userId: string): Promise<void> {
  let payload: SharedNotePayload;
  try {
    payload = await apiFetchOrThrow<SharedNotePayload>(`/public/share/${id}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      throw new SharedNoteError("notFound", "Shared note not found", err);
    }
    Sentry.captureException(err);
    throw new SharedNoteError("fetchFailed", "Failed to refresh shared note", err);
  }

  try {
    persistSharedNote(payload, userId, { skipRefIfMissing: true });
  } catch (err) {
    Sentry.captureException(err);
    throw new SharedNoteError(
      "storageFailed",
      "Failed to update shared note cache",
      err,
    );
  }
}

/**
 * Remove a shared note from the user's inbox. Drops the pointer row, the
 * content cache, and enqueues a sync delete so other devices catch up.
 */
export function removeShared(noteId: string, userId: string): void {
  const ref = queryOne<{ id: string }>(
    "SELECT id FROM shared_note_refs WHERE user_id = ? AND note_id = ?",
    [userId, noteId],
  );

  transaction(() => {
    execute("DELETE FROM shared_notes WHERE id = ?", [noteId]);
    if (ref) {
      execute("DELETE FROM shared_note_refs WHERE id = ?", [ref.id]);
    }
  });
  notifyChanges(["shared_note_refs", "shared_notes"]);

  if (ref) {
    enqueueSyncOp({
      type: "delete",
      entity: "shared_note_ref",
      entityId: ref.id,
      data: { id: ref.id, noteId },
      timestamp: Date.now(),
      retries: 0,
    });
  }
}

/** Read a cached shared note by id. */
export function getCachedSharedNote(id: string): SharedNoteRow | null {
  const row = queryOne<{
    id: string;
    title: string;
    content: string;
    author_name: string;
    created_at: string;
    updated_at: string;
    fetched_at: string;
    mentions_json: string | null;
  }>(
    `SELECT id, title, content, author_name, created_at, updated_at, fetched_at, mentions_json
     FROM shared_notes WHERE id = ?`,
    [id],
  );
  if (!row) return null;
  return {
    id: row.id,
    title: row.title,
    content: row.content,
    authorName: row.author_name,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    fetchedAt: row.fetched_at,
    mentionsJson: row.mentions_json,
  };
}

// ── Internal ─────────────────────────────────────────────────────────────

interface PersistOptions {
  /**
   * When true, do NOT create a new shared_note_refs row if one is missing.
   * Used by refreshShared() — refresh should not re-add an entry the user
   * just removed (or one cascaded away by the server).
   */
  skipRefIfMissing?: boolean;
}

function persistSharedNote(
  payload: SharedNotePayload,
  userId: string,
  options: PersistOptions = {},
): void {
  const fetchedAt = new Date().toISOString();
  const mentionsJson =
    payload.mentions && Object.keys(payload.mentions).length > 0
      ? JSON.stringify(payload.mentions)
      : null;

  const existingRef = queryOne<{ id: string }>(
    "SELECT id FROM shared_note_refs WHERE user_id = ? AND note_id = ?",
    [userId, payload.id],
  );

  let newRefId: string | null = null;
  if (!existingRef && !options.skipRefIfMissing) {
    newRefId = generateId();
  }

  transaction(() => {
    execute(
      `INSERT OR REPLACE INTO shared_notes (id, title, content, author_name, created_at, updated_at, fetched_at, mentions_json)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        payload.id,
        payload.title,
        payload.content,
        payload.authorName,
        payload.createdAt,
        payload.updatedAt,
        fetchedAt,
        mentionsJson,
      ],
    );

    if (newRefId) {
      execute(
        `INSERT INTO shared_note_refs (id, user_id, note_id, added_at, sync_status)
         VALUES (?, ?, ?, ?, 'pending')`,
        [newRefId, userId, payload.id, fetchedAt],
      );
    }
  });
  notifyChanges(["shared_notes", "shared_note_refs"]);

  if (newRefId) {
    enqueueSyncOp({
      type: "create",
      entity: "shared_note_ref",
      entityId: newRefId,
      data: { id: newRefId, noteId: payload.id, addedAt: fetchedAt },
      timestamp: Date.now(),
      retries: 0,
    });
  }
}
